import React, { useState, useEffect } from "react";
import { Axios, Link, Modal, Select } from "../../../Headers";
import { useFormik } from "formik";
import * as Yup from "yup";

const OrderDetails = () => {
  // State for Catalog / Category
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // State for Books
  const [books, setBooks] = useState([]);
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedBookId, setSelectedBookId] = useState("");
  const [loadingBooks, setLoadingBooks] = useState(false);

  // State for Selected Book Details
  const [bookDetails, setBookDetails] = useState(null);
  const [loadingBookDetails, setLoadingBookDetails] = useState(false);

  // State for Quantity
  const [quantity, setQuantity] = useState(1);

  // Multi-Book Order Cart State
  const [orderItems, setOrderItems] = useState([]);

  // Expand/Collapse toggles for About and Author
  const [isAboutExpanded, setIsAboutExpanded] = useState(false);
  const [isAuthorExpanded, setIsAuthorExpanded] = useState(false);

  // Modal feedback
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalStatus, setModalStatus] = useState({ success: false, message: "" });
  const [submitting, setSubmitting] = useState(false);

  // Fetch Catalogs / Categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = () => {
    setLoadingCategories(true);
    Axios.post("//" + window.$name + "/controller/GetWebCategoryController.php", {})
      .then((res) => {
        if (res.data && Array.isArray(res.data)) {
          setCategories(res.data);
        }
        setLoadingCategories(false);
      })
      .catch((err) => {
        console.error("Error fetching categories:", err);
        setLoadingCategories(false);
      });
  };

  // Fetch books when category changes
  const handleCategoryChange = (selectedOption) => {
    setSelectedCategory(selectedOption);
    setSelectedBook(null);
    setSelectedBookId("");
    setBookDetails(null);
    setBooks([]);

    if (!selectedOption || !selectedOption.value) return;

    const catId = selectedOption.value;
    setLoadingBooks(true);
    Axios.post("//" + window.$name + "/controller/GetWebBookTitleController.php", {
      category: catId,
    })
      .then((res) => {
        if (res.data && Array.isArray(res.data)) {
          setBooks(res.data);
        }
        setLoadingBooks(false);
      })
      .catch((err) => {
        console.error("Error fetching books:", err);
        setLoadingBooks(false);
      });
  };

  // Fetch book details when book is selected
  const handleBookChange = (selectedOption) => {
    setSelectedBook(selectedOption);
    const bookId = selectedOption ? selectedOption.value : "";
    setSelectedBookId(bookId);
    setBookDetails(null);
    setIsAboutExpanded(false);
    setIsAuthorExpanded(false);

    if (!bookId) return;

    setLoadingBookDetails(true);
    Axios.post("//" + window.$name + "/controller/GetWebBookByIdController.php", {
      bookId: bookId,
    })
      .then((res) => {
        if (res.data && res.data.length > 0) {
          setBookDetails(res.data[0]);
        }
        setLoadingBookDetails(false);
      })
      .catch((err) => {
        console.error("Error fetching book details:", err);
        setLoadingBookDetails(false);
      });
  };

  // Helper to extract pricing for a book object
  const getBookPricing = (details) => {
    if (!details) return { mrp: 0, salePrice: 0, effectivePrice: 0, shippingCharges: 0, discountPerBook: 0 };
    const mrp = parseInt(details.price, 10) || 0;
    const rawSale = details.sale_price !== undefined && details.sale_price !== null ? parseInt(details.sale_price, 10) : (details.salePrice !== undefined && details.salePrice !== null ? parseInt(details.salePrice, 10) : 0);
    const salePrice = (rawSale > 0) ? rawSale : mrp;
    const shippingCharges = parseInt(details.shipping_charges || details.shippingCharges || 0, 10) || 0;
    const discountPerBook = Math.max(0, mrp - salePrice);

    return {
      mrp,
      salePrice,
      effectivePrice: salePrice,
      shippingCharges,
      discountPerBook,
    };
  };

  // Preview Book Pricing
  const previewPricing = getBookPricing(bookDetails);
  const previewBookSubtotal = previewPricing.effectivePrice * (quantity || 1);
  const previewShippingSubtotal = previewPricing.shippingCharges * (quantity || 1);
  const previewGrandTotal = previewBookSubtotal + previewShippingSubtotal;
  const previewTotalSavings = previewPricing.discountPerBook * (quantity || 1);

  // Cart Totals
  const totalOrderBooksCount = orderItems.reduce((sum, item) => sum + item.quantity, 0);
  const totalOrderMrp = orderItems.reduce((sum, item) => sum + (item.mrp * item.quantity), 0);
  const totalOrderSaleSubtotal = orderItems.reduce((sum, item) => sum + (item.salePrice * item.quantity), 0);
  const totalOrderShipping = orderItems.reduce((sum, item) => sum + (item.shippingCharges * item.quantity), 0);
  const totalOrderDiscount = orderItems.reduce((sum, item) => sum + (item.discountSaved * item.quantity), 0);
  const totalOrderPrice = totalOrderSaleSubtotal + totalOrderShipping; // Grand Total

  // Add Selected Book to Multi-Book Order Cart
  const handleAddBookToOrder = () => {
    if (!bookDetails || !selectedBookId) return;

    const targetBookIdStr = String(bookDetails.id || selectedBookId);
    const pricing = getBookPricing(bookDetails);

    if (totalOrderBooksCount + quantity > 10) {
      setModalStatus({
        success: false,
        message: `Cannot add book. Maximum order limit is 10 books per order. (Currently added: ${totalOrderBooksCount} books).`,
      });
      setModalIsOpen(true);
      return;
    }

    const existingIndex = orderItems.findIndex((i) => String(i.bookId) === targetBookIdStr);
    if (existingIndex > -1) {
      const currentQty = orderItems[existingIndex].quantity;
      const newQty = currentQty + quantity;

      if (newQty > 2) {
        setModalStatus({
          success: false,
          message: `Maximum 2 copies allowed per single book title. (You already have ${currentQty} copy/copies of "${bookDetails.title}" in your order).`,
        });
        setModalIsOpen(true);
        return;
      }

      if (totalOrderBooksCount + quantity > 10) {
        setModalStatus({
          success: false,
          message: `Cannot add more copies. Total order limit is 10 books per order.`,
        });
        setModalIsOpen(true);
        return;
      }
      const updated = [...orderItems];
      const itemMrp = updated[existingIndex].mrp || pricing.mrp;
      const itemSale = updated[existingIndex].salePrice || pricing.salePrice;
      const itemShipping = updated[existingIndex].shippingCharges || pricing.shippingCharges;
      const itemDiscount = updated[existingIndex].discountSaved || pricing.discountPerBook;

      updated[existingIndex].quantity = newQty;
      updated[existingIndex].bookSubtotal = itemSale * newQty;
      updated[existingIndex].shippingSubtotal = itemShipping * newQty;
      updated[existingIndex].subtotal = (itemSale * newQty) + (itemShipping * newQty);
      updated[existingIndex].totalSavings = itemDiscount * newQty;
      setOrderItems(updated);
    } else {
      if (quantity > 2) {
        setModalStatus({
          success: false,
          message: `Maximum 2 copies allowed per single book title.`,
        });
        setModalIsOpen(true);
        return;
      }

      setOrderItems([
        ...orderItems,
        {
          bookId: targetBookIdStr,
          title: bookDetails.title,
          isbn: bookDetails.isbn || "",
          mrp: pricing.mrp,
          salePrice: pricing.salePrice,
          unitPrice: pricing.salePrice,
          shippingCharges: pricing.shippingCharges,
          discountSaved: pricing.discountPerBook,
          quantity: quantity,
          bookSubtotal: pricing.salePrice * quantity,
          shippingSubtotal: pricing.shippingCharges * quantity,
          subtotal: (pricing.salePrice * quantity) + (pricing.shippingCharges * quantity),
          totalSavings: pricing.discountPerBook * quantity,
        },
      ]);
    }

    // Reset book selection for adding next book
    setSelectedBook(null);
    setSelectedBookId("");
    setBookDetails(null);
    setQuantity(1);
  };

  const handleRemoveOrderItem = (bookId) => {
    setOrderItems(orderItems.filter((i) => String(i.bookId) !== String(bookId)));
  };

  const handleUpdateOrderItemQty = (bookId, newQty) => {
    if (newQty < 1) return;
    const targetBookIdStr = String(bookId);

    if (newQty > 2) {
      setModalStatus({
        success: false,
        message: `Maximum 2 copies allowed per single book title.`,
      });
      setModalIsOpen(true);
      return;
    }

    const currentQtyWithoutThis = orderItems
      .filter((i) => String(i.bookId) !== targetBookIdStr)
      .reduce((sum, i) => sum + i.quantity, 0);

    if (currentQtyWithoutThis + newQty > 10) {
      setModalStatus({
        success: false,
        message: `Maximum total limit is 10 books per order.`,
      });
      setModalIsOpen(true);
      return;
    }

    setOrderItems(
      orderItems.map((i) => {
        if (String(i.bookId) === targetBookIdStr) {
          const itemMrp = i.mrp || 0;
          const itemSale = i.salePrice || i.unitPrice || 0;
          const itemShipping = i.shippingCharges || 0;
          const itemDiscount = i.discountSaved || 0;

          return {
            ...i,
            quantity: newQty,
            bookSubtotal: itemSale * newQty,
            shippingSubtotal: itemShipping * newQty,
            subtotal: (itemSale * newQty) + (itemShipping * newQty),
            totalSavings: itemDiscount * newQty,
          };
        }
        return i;
      })
    );
  };

  // Formik for user details & order submission
  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      mobile: "",
      address: "",
      city: "",
      state: "",
      pincode: "",
      transactionId: "",
      paymentNote: "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string()
        .required("First name is required")
        .min(2, "First name must be at least 2 characters"),
      lastName: Yup.string()
        .required("Last name is required")
        .min(2, "Last name must be at least 2 characters"),
      mobile: Yup.string()
        .required("Mobile number is required")
        .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits"),
      email: Yup.string()
        .required("Email is required")
        .email("Invalid email address"),
      address: Yup.string()
        .required("Delivery address is required")
        .min(5, "Address must be at least 5 characters"),
      city: Yup.string()
        .required("City is required")
        .min(2, "City must be at least 2 characters"),
      state: Yup.string()
        .required("State is required")
        .min(2, "State must be at least 2 characters"),
      pincode: Yup.string()
        .required("Pincode is required")
        .matches(/^[0-9]{6}$/, "Pincode must be 6 digits"),
      transactionId: Yup.string()
        .required("Transaction ID is required for payment verification")
        .min(3, "Transaction ID must be at least 3 characters"),
      paymentNote: Yup.string().optional(),
    }),
    onSubmit: (values, { resetForm }) => {
      let finalItems = [...orderItems];

      // Auto-include preview book only if orderItems is currently empty
      if (finalItems.length === 0 && bookDetails && selectedBookId) {
        const pricing = getBookPricing(bookDetails);
        finalItems.push({
          bookId: String(bookDetails.id || selectedBookId),
          title: bookDetails.title,
          isbn: bookDetails.isbn || "",
          mrp: pricing.mrp,
          salePrice: pricing.salePrice,
          unitPrice: pricing.salePrice,
          shippingCharges: pricing.shippingCharges,
          discountSaved: pricing.discountPerBook,
          quantity: quantity,
          bookSubtotal: pricing.salePrice * quantity,
          shippingSubtotal: pricing.shippingCharges * quantity,
          subtotal: (pricing.salePrice * quantity) + (pricing.shippingCharges * quantity),
          totalSavings: pricing.discountPerBook * quantity,
        });
      }

      if (finalItems.length === 0) {
        setModalStatus({
          success: false,
          message: "Please select and add at least one book to your order.",
        });
        setModalIsOpen(true);
        return;
      }

      const finalTotalBooks = finalItems.reduce((s, i) => s + i.quantity, 0);
      const finalTotalPrice = finalItems.reduce((s, i) => s + i.subtotal, 0);

      if (finalTotalBooks > 10) {
        setModalStatus({
          success: false,
          message: `Maximum limit per order is 10 books. You currently have ${finalTotalBooks} books selected.`,
        });
        setModalIsOpen(true);
        return;
      }

      setSubmitting(true);
      const fd = new FormData();
      fd.append("firstName", values.firstName);
      fd.append("lastName", values.lastName);
      fd.append("email", values.email);
      fd.append("mobile", values.mobile);
      fd.append("bookId", finalItems[0]?.bookId || selectedBookId);
      fd.append("copy", finalTotalBooks);
      fd.append("address", values.address);
      fd.append("city", values.city);
      fd.append("state", values.state);
      fd.append("pincode", values.pincode);
      fd.append("totalPrice", finalTotalPrice);
      fd.append("transactionId", values.transactionId);
      fd.append("paymentNote", values.paymentNote);
      fd.append("items", JSON.stringify(finalItems));

      Axios.post("//" + window.$name + "/controller/AddWebBookOrderController.php", fd)
        .then((res) => {
          setSubmitting(false);
          if (res.data && res.data.Success === 1) {
            const ordNum = res.data.value?.orderNumber || "ORD" + Math.floor(100000 + Math.random() * 900000);
            setModalStatus({
              success: true,
              message: `Order Placed Successfully! Your Order ID is "${ordNum}". Total ${finalTotalBooks} books (Total ₹${finalTotalPrice}) order has been submitted.`,
            });
            setModalIsOpen(true);
            resetForm();
            setSelectedCategory(null);
            setSelectedBook(null);
            setSelectedBookId("");
            setBookDetails(null);
            setQuantity(1);
            setOrderItems([]);
          } else {
            setModalStatus({
              success: false,
              message: res.data?.value?.Msg || "There was an error processing your order.",
            });
            setModalIsOpen(true);
          }
        })
        .catch((err) => {
          setSubmitting(false);
          setModalStatus({
            success: false,
            message: "Network error occurred while submitting order.",
          });
          setModalIsOpen(true);
        });
    },
  });

  return (
    <div className="container py-4">
      {/* Custom Styles for Disabled Buttons */}
      <style>{`
        .btn:disabled, .btn[disabled] {
          opacity: 0.55 !important;
          cursor: not-allowed !important;
          pointer-events: auto !important;
          filter: grayscale(35%);
          box-shadow: none !important;
        }
        .btn-outline-secondary:disabled, .btn-outline-secondary[disabled] {
          background-color: #e9ecef !important;
          color: #adb5bd !important;
          border-color: #ced4da !important;
        }
        .btn-success:disabled, .btn-success[disabled] {
          background-color: #81c784 !important;
          border-color: #81c784 !important;
          color: #ffffff !important;
        }
        .btn-primary:disabled, .btn-primary[disabled] {
          background-color: #90caf9 !important;
          border-color: #90caf9 !important;
          color: #ffffff !important;
        }
      `}</style>

      {/* Header Banner */}
      <div className="row mb-4">
        <div className="col-12 text-center">
          <h2 style={{ fontWeight: "700", color: "#1a252f" }}>Book Order Form</h2>
          <p className="text-muted">Select catalog, choose a book, and fill in your details to place an order</p>
          <div
            style={{
              borderTop: "3px solid #007bff",
              width: "80px",
              margin: "10px auto 20px auto",
              borderRadius: "2px",
            }}
          ></div>
        </div>
      </div>

      <form onSubmit={formik.handleSubmit}>
        <div className="row">
          {/* Left Column - Selection & Book Details */}
          <div className="col-lg-6 mb-4">
            <div className="card shadow-sm border-0 rounded-lg p-4">
              <h4 className="mb-3 text-primary">
                <i className="fa fa-book mr-2"></i>1. Select Catalog & Book
              </h4>

              {/* Catalog Selection */}
              <div className="form-group mb-3">
                <label className="font-weight-bold">
                  Catalog / Category <span className="text-danger">*</span>
                </label>
                <Select
                  options={categories.map((cat) => ({
                    value: cat.value || cat.id,
                    label: cat.label || cat.category,
                  }))}
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  isDisabled={loadingCategories}
                  isLoading={loadingCategories}
                  isSearchable={true}
                  isClearable={true}
                  placeholder="-- Search or Select Catalog --"
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 99999 }) }}
                />
              </div>

              {/* Book Selection */}
              <div className="form-group mb-3">
                <label className="font-weight-bold">
                  Select Book <span className="text-danger">*</span>
                </label>
                <Select
                  options={books.map((book) => ({
                    value: book.id || book.bookId,
                    label: book.title,
                  }))}
                  value={selectedBook}
                  onChange={handleBookChange}
                  isDisabled={!selectedCategory || loadingBooks}
                  isLoading={loadingBooks}
                  isSearchable={true}
                  isClearable={true}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 99999 }) }}
                  placeholder={
                    !selectedCategory
                      ? "-- First Select a Catalog --"
                      : books.length === 0
                      ? "-- No Books Available --"
                      : "-- Search or Select Book --"
                  }
                />
              </div>

              {/* Book Details Preview */}
              {loadingBookDetails && (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading book details...</span>
                  </div>
                  <p className="mt-2 text-muted">Fetching book details...</p>
                </div>
              )}

              {bookDetails && !loadingBookDetails && (
                <div className="mt-3 p-3 bg-light rounded border">
                  <h5 className="text-dark font-weight-bold mb-3">{bookDetails.title}</h5>
                  <div className="row">
                    {bookDetails.image && (
                      <div className="col-md-4 mb-2">
                        <img
                          src={`${window.$file}webBooks/${bookDetails.image}`}
                          alt={bookDetails.title}
                          className="img-fluid rounded shadow-sm"
                          style={{ maxHeight: "160px", objectFit: "cover", width: "100%" }}
                        />
                      </div>
                    )}
                    <div className={bookDetails.image ? "col-md-8" : "col-12"}>
                      {bookDetails.isbn && (
                        <p className="mb-1">
                          <strong>ISBN:</strong> {bookDetails.isbn}
                        </p>
                      )}
                      {bookDetails.edition && (
                        <p className="mb-1">
                          <strong>Edition:</strong> {bookDetails.edition}
                        </p>
                      )}
                      {/* Pricing Details: MRP, Sale Price, Savings, Shipping */}
                      <div className="mb-2">
                        {previewPricing.discountPerBook > 0 ? (
                          <div>
                            <span className="text-muted mr-2" style={{ textDecoration: "line-through", fontSize: "1.1rem" }}>
                              MRP: ₹{previewPricing.mrp}
                            </span>
                            <span className="text-success h4 font-weight-bold">
                              Offer Price: ₹{previewPricing.salePrice}
                            </span>
                            <span className="badge badge-success p-2 ml-2 font-weight-bold">
                              🎉 Save ₹{previewPricing.discountPerBook} ({Math.round((previewPricing.discountPerBook / previewPricing.mrp) * 100)}% OFF)
                            </span>
                          </div>
                        ) : (
                          <span className="text-success h4 font-weight-bold">
                            Price: ₹{previewPricing.mrp}
                          </span>
                        )}
                        <div className="mt-1 text-info font-weight-bold small">
                          <i className="fa fa-truck mr-1"></i>
                          {previewPricing.shippingCharges > 0
                            ? `Shipping: ₹${previewPricing.shippingCharges} per book`
                            : "FREE Shipping"}
                        </div>
                      </div>

                      {/* Author Details with Read More / Expand */}
                      {bookDetails.author && (
                        <div className="mb-2">
                          <strong className="text-dark">Author: </strong>
                          <span
                            className="text-secondary"
                            style={
                              !isAuthorExpanded && bookDetails.author.length > 100
                                ? {
                                    display: "-webkit-box",
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }
                                : {}
                            }
                          >
                            {bookDetails.author}
                          </span>
                          {bookDetails.author.length > 100 && (
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 ml-1 font-weight-bold text-primary"
                              onClick={() => setIsAuthorExpanded(!isAuthorExpanded)}
                              style={{ textDecoration: "none" }}
                            >
                              {isAuthorExpanded ? " Show Less ▲" : " Read More ▼"}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* About Book Accordion / Read More Section */}
                  {bookDetails.about && (
                    <div className="mt-3 border rounded overflow-hidden">
                      <div
                        className="bg-white p-2 px-3 d-flex justify-content-between align-items-center border-bottom"
                        style={{ cursor: "pointer", userSelect: "none" }}
                        onClick={() => setIsAboutExpanded(!isAboutExpanded)}
                      >
                        <span className="font-weight-bold text-dark">
                          <i className="fa fa-info-circle text-primary mr-2"></i>About the Book
                        </span>
                        <span className="text-primary font-weight-bold small">
                          {isAboutExpanded ? "Hide Details ▲" : "Read More ▼"}
                        </span>
                      </div>
                      <div className="p-3 bg-white">
                        <p
                          className="mb-0 text-secondary"
                          style={
                            !isAboutExpanded
                              ? {
                                  display: "-webkit-box",
                                  WebkitLineClamp: 4,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }
                              : {}
                          }
                        >
                          {bookDetails.about}
                        </p>
                        {bookDetails.about.length > 150 && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 mt-2 font-weight-bold text-primary"
                            onClick={() => setIsAboutExpanded(!isAboutExpanded)}
                            style={{ textDecoration: "none" }}
                          >
                            {isAboutExpanded ? "Show Less ▲" : "Read More / Show All ▼"}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Quantity Selection & Add to Order Button */}
              {bookDetails && (
                <div className="mt-4 pt-3 border-top">
                  <h5 className="text-primary mb-3">2. Book Quantity & Add to Order</h5>
                  <div className="form-group row align-items-center">
                    <label className="col-sm-5 col-form-label font-weight-bold">
                      Select Copies:
                    </label>
                    <div className="col-sm-7">
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setQuantity(Math.max(1, quantity - 1))}
                            disabled={quantity <= 1}
                            title={quantity <= 1 ? "Minimum 1 copy required" : ""}
                          >
                            -
                          </button>
                        </div>
                        <input
                          type="number"
                          min="1"
                          max="2"
                          className="form-control text-center font-weight-bold"
                          value={quantity}
                          onChange={(e) =>
                            setQuantity(Math.min(2, Math.max(1, parseInt(e.target.value) || 1)))
                          }
                        />
                        <div className="input-group-append">
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setQuantity(Math.min(2, quantity + 1))}
                            disabled={quantity >= 2 || totalOrderBooksCount + quantity >= 10}
                            title={
                              quantity >= 2
                                ? "Maximum 2 copies allowed per title"
                                : totalOrderBooksCount + quantity >= 10
                                ? "Maximum 10 total books per order"
                                : ""
                            }
                          >
                            +
                          </button>
                        </div>
                      </div>
                      <small className="text-muted d-block mt-1">Up to 2 copies per title (Max 10 total books per order).</small>
                    </div>
                  </div>

                  {/* Item Pricing Summary Preview Box */}
                  <div className="p-3 bg-white rounded border mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="text-muted small">Sale Price Subtotal ({quantity} {quantity === 1 ? "copy" : "copies"}):</span>
                      <span className="font-weight-bold text-dark">₹{previewBookSubtotal}</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="text-muted small">Shipping Charges:</span>
                      <span className="font-weight-bold text-info">
                        {previewShippingSubtotal > 0 ? `+ ₹${previewShippingSubtotal}` : "FREE"}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                      <strong className="text-dark">Total for this book:</strong>
                      <span className="h4 text-primary font-weight-bold mb-0">₹{previewGrandTotal}</span>
                    </div>

                    {previewTotalSavings > 0 && (
                      <div className="alert alert-success mt-2 py-1 px-2 mb-0 small font-weight-bold text-center">
                        🎉 You save ₹{previewTotalSavings} on this title!
                      </div>
                    )}
                  </div>

                  {/* Add to Order Button */}
                  <button
                    type="button"
                    className="btn btn-success btn-block mt-3 font-weight-bold shadow-sm"
                    onClick={handleAddBookToOrder}
                    disabled={totalOrderBooksCount + quantity > 10}
                    title={totalOrderBooksCount + quantity > 10 ? "Order limit reached (10 books max)" : ""}
                  >
                    <i className="fa fa-plus-circle mr-2"></i>Add Book to Order
                  </button>
                </div>
              )}

              {/* Selected Books Cart Table */}
              {orderItems.length > 0 && (
                <div className="mt-4 border rounded p-3 bg-white shadow-sm">
                  <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                    <h5 className="text-primary font-weight-bold mb-0">
                      <i className="fa fa-shopping-basket mr-2"></i>Selected Books ({orderItems.length})
                    </h5>
                    <span className={`badge ${totalOrderBooksCount >= 10 ? "badge-danger" : "badge-info"} px-2 py-1`}>
                      Total Books: {totalOrderBooksCount} / 10 Max
                    </span>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-bordered table-sm mb-2">
                      <thead className="thead-light">
                        <tr>
                          <th>Book Title</th>
                          <th>Price Breakdown</th>
                          <th>Qty</th>
                          <th>Subtotal</th>
                          <th style={{ width: "40px" }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderItems.map((item, idx) => (
                          <tr key={idx}>
                            <td className="font-weight-bold text-dark">{item.title}</td>
                            <td>
                              <div>
                                {item.discountSaved > 0 && (
                                  <span className="text-muted mr-1 small" style={{ textDecoration: "line-through" }}>
                                    ₹{item.mrp}
                                  </span>
                                )}
                                <span className="font-weight-bold text-success">₹{item.salePrice}</span>
                              </div>
                              <small className="text-info d-block">
                                {item.shippingCharges > 0 ? `+ ₹${item.shippingCharges} ship/book` : "Free Shipping"}
                              </small>
                            </td>
                            <td>
                              <div className="input-group input-group-sm" style={{ width: "90px" }}>
                                <div className="input-group-prepend">
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary px-1"
                                    onClick={() => handleUpdateOrderItemQty(item.bookId, item.quantity - 1)}
                                  >
                                    -
                                  </button>
                                </div>
                                <span className="form-control text-center font-weight-bold px-1">{item.quantity}</span>
                                <div className="input-group-append">
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary px-1"
                                    onClick={() => handleUpdateOrderItemQty(item.bookId, item.quantity + 1)}
                                    disabled={item.quantity >= 2 || totalOrderBooksCount >= 10}
                                    title={
                                      item.quantity >= 2
                                        ? "Maximum 2 copies allowed per title"
                                        : totalOrderBooksCount >= 10
                                        ? "Maximum 10 total books per order"
                                        : ""
                                    }
                                  >
                                    +
                                  </button>
                                </div>
                              </div>
                            </td>
                            <td className="font-weight-bold text-success">₹{item.subtotal}</td>
                            <td className="text-center">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger p-1"
                                onClick={() => handleRemoveOrderItem(item.bookId)}
                                title="Remove Book"
                              >
                                &times;
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Order Totals Breakdown Card */}
                  <div className="p-3 bg-light rounded border mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="text-muted small">Total MRP ({totalOrderBooksCount} books):</span>
                      <span className="text-muted small" style={{ textDecoration: "line-through" }}>₹{totalOrderMrp}</span>
                    </div>

                    {totalOrderDiscount > 0 && (
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="text-success font-weight-bold small">Special Discount Saved:</span>
                        <span className="text-success font-weight-bold small">- ₹{totalOrderDiscount}</span>
                      </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="text-muted small">Sale Subtotal:</span>
                      <span className="font-weight-bold text-dark">₹{totalOrderSaleSubtotal}</span>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted small">Total Shipping Charges:</span>
                      <span className="font-weight-bold text-info">
                        {totalOrderShipping > 0 ? `+ ₹${totalOrderShipping}` : "FREE"}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                      <div>
                        <strong className="text-dark">Grand Total Payable:</strong>
                        <small className="text-muted d-block">Includes all books & shipping</small>
                      </div>
                      <span className="h4 text-success font-weight-bold mb-0">₹{totalOrderPrice}</span>
                    </div>

                    {totalOrderDiscount > 0 && (
                      <div className="alert alert-success mt-2 py-2 px-3 mb-0 text-center font-weight-bold shadow-sm">
                        🎉 Total Savings: You are saving ₹{totalOrderDiscount} on this order!
                      </div>
                    )}
                  </div>

                  {/* Payment QR Code Box */}
                  <div className="mt-3 p-3 bg-white border rounded text-center shadow-sm">
                    <h6 className="font-weight-bold text-primary mb-2">
                      <i className="fa fa-qrcode mr-2"></i>Scan QR Code to Pay ₹{totalOrderPrice}
                    </h6>
                    <div className="my-2">
                      <img
                        src={process.env.PUBLIC_URL + "/assets/data/Sortsol Payment scanner.jpg.jpeg"}
                        alt="SortSol Payment QR Scanner"
                        className="img-fluid rounded border shadow-sm"
                        style={{ maxWidth: "200px", height: "auto" }}
                      />
                    </div>
                    <small className="text-muted d-block">
                      Scan using any UPI App (Google Pay, PhonePe, Paytm, BHIM) and enter the Transaction ID on the right after completing payment.
                    </small>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - User Details & Submit */}
          <div className="col-lg-6 mb-4">
            <div className="card shadow-sm border-0 rounded-lg p-4">
              <h4 className="mb-3 text-primary">
                <i className="fa fa-user mr-2"></i>3. Customer Details
              </h4>

              {/* First Name */}
              <div className="form-group mb-3">
                <label className="font-weight-bold">
                  First Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  className="form-control"
                  placeholder="Enter first name"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.firstName && formik.errors.firstName && (
                  <div className="text-danger small mt-1">{formik.errors.firstName}</div>
                )}
              </div>

              {/* Last Name */}
              <div className="form-group mb-3">
                <label className="font-weight-bold">
                  Last Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  className="form-control"
                  placeholder="Enter last name"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.lastName && formik.errors.lastName && (
                  <div className="text-danger small mt-1">{formik.errors.lastName}</div>
                )}
              </div>

              {/* Email */}
              <div className="form-group mb-3">
                <label className="font-weight-bold">
                  Email Address <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  placeholder="Enter email address"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.email && formik.errors.email && (
                  <div className="text-danger small mt-1">{formik.errors.email}</div>
                )}
              </div>

              {/* Mobile */}
              <div className="form-group mb-3">
                <label className="font-weight-bold">
                  Mobile Number <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="mobile"
                  className="form-control"
                  placeholder="Enter 10-digit mobile number"
                  value={formik.values.mobile}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.mobile && formik.errors.mobile && (
                  <div className="text-danger small mt-1">{formik.errors.mobile}</div>
                )}
              </div>

              {/* City and State */}
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label className="font-weight-bold">
                      City <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="city"
                      className="form-control"
                      placeholder="Enter city"
                      value={formik.values.city}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.city && formik.errors.city && (
                      <div className="text-danger small mt-1">{formik.errors.city}</div>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label className="font-weight-bold">
                      State <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="state"
                      className="form-control"
                      placeholder="Enter state"
                      value={formik.values.state}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.state && formik.errors.state && (
                      <div className="text-danger small mt-1">{formik.errors.state}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Pincode */}
              <div className="form-group mb-3">
                <label className="font-weight-bold">
                  Pincode / Postal Code <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="pincode"
                  className="form-control"
                  placeholder="Enter 6-digit pincode"
                  value={formik.values.pincode}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.pincode && formik.errors.pincode && (
                  <div className="text-danger small mt-1">{formik.errors.pincode}</div>
                )}
              </div>

              {/* Address */}
              <div className="form-group mb-3">
                <label className="font-weight-bold">
                  Delivery Address <span className="text-danger">*</span>
                </label>
                <textarea
                  name="address"
                  rows="3"
                  className="form-control"
                  placeholder="Enter full street address, house no., landmark"
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                ></textarea>
                {formik.touched.address && formik.errors.address && (
                  <div className="text-danger small mt-1">{formik.errors.address}</div>
                )}
              </div>

              {/* Transaction ID & Optional Payment Note */}
              <div className="form-group mb-4 p-3 bg-light rounded border">
                <label className="font-weight-bold text-dark">
                  Payment Transaction ID <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="transactionId"
                  className="form-control mb-3"
                  placeholder="Enter Payment Transaction ID (e.g. TXN123456789)"
                  value={formik.values.transactionId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.transactionId && formik.errors.transactionId && (
                  <div className="text-danger small mb-3">{formik.errors.transactionId}</div>
                )}

                <label className="font-weight-bold text-dark">
                  Payment / Additional Note <span className="text-muted font-weight-normal">(Optional)</span>
                </label>
                <textarea
                  name="paymentNote"
                  rows="2"
                  className="form-control"
                  placeholder="Any additional note regarding payment or transaction..."
                  value={formik.values.paymentNote}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                ></textarea>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="btn btn-primary btn-block btn-lg font-weight-bold shadow-sm"
                disabled={submitting || (orderItems.length === 0 && !selectedBookId)}
                title={
                  submitting
                    ? "Submitting order..."
                    : orderItems.length === 0 && !selectedBookId
                    ? "Please select or add a book to place an order"
                    : ""
                }
              >
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                    Placing Order...
                  </>
                ) : (
                  "Place Order Now"
                )}
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* Confirmation Modal */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={() => setModalIsOpen(false)}
        contentLabel="Order Status"
        style={{
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            maxWidth: "500px",
            width: "90%",
            borderRadius: "10px",
            padding: "25px",
          },
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1050,
          },
        }}
      >
        <div className="text-center">
          <div className="mb-3">
            {modalStatus.success ? (
              <i className="fa fa-check-circle text-success" style={{ fontSize: "50px" }}></i>
            ) : (
              <i className="fa fa-exclamation-circle text-danger" style={{ fontSize: "50px" }}></i>
            )}
          </div>
          <h4 className={modalStatus.success ? "text-success" : "text-danger"}>
            {modalStatus.success ? "Order Submitted!" : "Submission Failed"}
          </h4>
          <p className="mt-3 text-secondary">{modalStatus.message}</p>
          <button
            className="btn btn-primary px-4 mt-3"
            onClick={() => setModalIsOpen(false)}
          >
            Close
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default OrderDetails;
